import type { ButtonHTMLAttributes, ReactNode } from 'react';
import { cn } from './cn';

type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger' | 'link';
type ButtonSize = 'sm' | 'md' | 'icon';

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  leftIcon?: ReactNode;
  rightIcon?: ReactNode;
}

const variants: Record<ButtonVariant, string> = {
  primary:
    'border-text bg-text text-white ring-transparent shadow-[var(--shadow-control)] hover:border-accent hover:bg-accent hover:text-text active:translate-y-px focus:ring-accent',
  secondary:
    'border-border/40 bg-white/80 text-text ring-transparent hover:border-text hover:bg-text hover:text-white active:translate-y-px focus:ring-accent',
  ghost:
    'border-transparent bg-transparent text-muted ring-transparent hover:bg-shade hover:text-text active:translate-y-px focus:ring-accent',
  danger:
    'border-danger/30 bg-white text-danger ring-transparent hover:bg-danger hover:text-text active:translate-y-px focus:ring-danger',
  link:
    'border-transparent bg-transparent p-0 text-text underline decoration-2 underline-offset-4 ring-transparent hover:text-accent focus:ring-accent',
};

const sizes: Record<ButtonSize, string> = {
  sm: 'px-3.5 py-2 text-xs',
  md: 'px-5 py-3 text-sm',
  icon: 'h-9 w-9 p-0',
};

export const Button = ({
  className,
  variant = 'secondary',
  size = 'sm',
  leftIcon,
  rightIcon,
  children,
  type = 'button',
  ...props
}: ButtonProps) => (
  <button
    type={type}
    className={cn(
      'inline-flex items-center justify-center gap-2 rounded-control border font-semibold ring-1 ring-inset transition-all focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-bg disabled:cursor-not-allowed disabled:opacity-45',
      variants[variant],
      sizes[size],
      className
    )}
    {...props}
  >
    {leftIcon}
    {children}
    {rightIcon}
  </button>
);
